import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import { Spacer } from "@coworker/apprestructured/src/shared/components/Spacer/Spacer";
import { ItemDelete } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemDelete/ItemDelete";
import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import { Media } from "@coworker/app/src/components/RoomSettings/types/media";
import { ItemQuantityStepper } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemQuantityStepper/ItemQuantityStepper";
import FixaLink from "@coworker/apprestructured/src/shared/wrappers/FixaLink/FixaLink";
import { ItemCard } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCard/ItemCard";
import {
  Article,
  EdsData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { ItemEditQuantity } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemEditQuantity/ItemEditQuantity";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaItemCardLocalCombinationInnerChild } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardLocalCombination/MediaItemCardLocalCombinationInnerChild";
import styles from "../../../styles/common-media-styles.module.css";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";

type MediaItemCardLocalCombinationBaseProps<
  T extends AggregatedArticle | Article,
> = {
  article: T;
  itemCardRenderType?: ItemCardRenderType;
  edsData: EdsData | null;
  badges?: ItemBadge[];
  isItemInGroups?: boolean;
  mediaItemViewType?: MediaItemViewType;
  totalQuantityPieces?: number;
  shouldDisplayQuantity?: boolean;
};

type MediaItemCardLocalCombinationProps<T extends AggregatedArticle | Article> =
  | (MediaItemCardLocalCombinationBaseProps<T> & {
      showActions?: false;
      media?: Media;
    })
  | (MediaItemCardLocalCombinationBaseProps<T> & {
      showActions: true;
      media: Media;
    });

export const MediaItemCardLocalCombination = <
  T extends AggregatedArticle | Article,
>(
  props: MediaItemCardLocalCombinationProps<T>
) => {
  const {
    article,
    edsData,
    showActions = false,
    badges,
    isItemInGroups = false,
    itemCardRenderType = ItemCardRenderType.link,
    mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
    totalQuantityPieces: totalQuantityPiecesProp,
    shouldDisplayQuantity = false,
  } = props;
  const media = showActions ? props.media : undefined;
  const totalQuantityPieces =
    totalQuantityPiecesProp ??
    (isItemInGroups
      ? (article as AggregatedArticle)?.aggregatedArticles.reduce(
          (acc, a) => acc + a.nbrArticles,
          0
        )
      : 0);

  const combinedBadges = badges?.concat({
    text: "Local combination/SPR",
    colour: "white",
  });
  const navigationUrl =
    mediaItemViewType === MediaItemViewType.AGGREGATED_ITEM_VIEW
      ? `/mediamaintenance/medias/${article.mediaId}/articles/${article.id}/product_article_id/${article.productArticleId}`
      : `/mediamaintenance/articles/${article.id}`;
  return (
    <ItemCard
      badges={combinedBadges || []}
      dataTestId={"media-item-card-local-combination"}
    >
      {itemCardRenderType === ItemCardRenderType.link ? (
        <FixaLink to={navigationUrl} className={styles["item-card-root-row"]}>
          <MediaItemCardLocalCombinationInnerChild
            article={article}
            shouldDisplayQuantity={isItemInGroups || shouldDisplayQuantity}
            totalQuantityPieces={totalQuantityPieces}
            edsData={edsData}
          />
        </FixaLink>
      ) : (
        <div className={styles["item-card-root-row"]}>
          <MediaItemCardLocalCombinationInnerChild
            itemCardRenderType={itemCardRenderType}
            article={article}
            shouldDisplayQuantity={isItemInGroups || shouldDisplayQuantity}
            totalQuantityPieces={totalQuantityPieces}
            edsData={edsData}
          />
        </div>
      )}

      {showActions && media && (
        <>
          <Spacer />
          <div className={styles["item-card-action-row"]}>
            {isItemInGroups ? (
              <ItemEditQuantity
                media={media}
                article={article as AggregatedArticle}
              />
            ) : (
              <ItemQuantityStepper
                itemNo={article.productArticleId}
                article={article}
                itemQuantityPieces={article.nbrArticles}
              />
            )}

            <ItemDelete
              itemNo={article.productArticleId}
              isItemInMultipleGroups={isItemInGroups}
              article={article}
              media={media}
            />
          </div>
        </>
      )}
    </ItemCard>
  );
};
